/**
 * Feature Access Control Configuration
 * Defines which features are available per subscription tier
 * and which locked features should show premium previews
 */

export const SUBSCRIPTION_TIERS = {
  TRIAL: 'trial',
  PRO: 'pro',
  ELITE: 'elite',
  CREATOR: 'creator',
  CREATOR_ELITE: 'creator_elite',
  FOUNDER: 'founder'
};

export const FEATURE_ACCESS = {
  // Core features - available to Pro and above
  'app/dashboard': { minTier: SUBSCRIPTION_TIERS.PRO },
  'home-dashboard': { minTier: SUBSCRIPTION_TIERS.PRO },
  'app/daily-drill': { minTier: SUBSCRIPTION_TIERS.PRO },
  'daily-drill': { minTier: SUBSCRIPTION_TIERS.PRO },
  'app/settings': { minTier: SUBSCRIPTION_TIERS.PRO },
  'settings': { minTier: SUBSCRIPTION_TIERS.PRO },
  'app/industry-selection': { minTier: SUBSCRIPTION_TIERS.PRO },
  'industry-selection': { minTier: SUBSCRIPTION_TIERS.PRO },
  
  // Pro tier features
  'app/leaderboards': { minTier: SUBSCRIPTION_TIERS.PRO },
  'leaderboards': { minTier: SUBSCRIPTION_TIERS.PRO },
  'app/objection-dojo': { minTier: SUBSCRIPTION_TIERS.PRO, showPreview: true },
  'objection-dojo': { minTier: SUBSCRIPTION_TIERS.PRO, showPreview: true },
  'app/script-lab': { minTier: SUBSCRIPTION_TIERS.PRO },
  'script-lab': { minTier: SUBSCRIPTION_TIERS.PRO },
  
  // Elite tier features
  'app/ai-roleplay': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: true },
  'app/conversation-analyzer': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: true },
  'conversation-analyzer': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: true },
  'ai-roleplay': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: true },
  'app/lumo-progress': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: false },
  'lumo-progress': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: false },
  'app/techniques-library': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: false },
  'techniques-library': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: false },
  'app/cascade-challenge': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: false },
  'cascade-challenge': { minTier: SUBSCRIPTION_TIERS.ELITE, showPreview: false },
  'app/gamification': { minTier: SUBSCRIPTION_TIERS.ELITE },
  'gamification': { minTier: SUBSCRIPTION_TIERS.ELITE },
  'app/prestige-drills': { minTier: SUBSCRIPTION_TIERS.ELITE },
  'prestige-drills': { minTier: SUBSCRIPTION_TIERS.ELITE },
  'app/micro-lessons': { minTier: SUBSCRIPTION_TIERS.ELITE },
  'micro-lessons': { minTier: SUBSCRIPTION_TIERS.ELITE },
  'app/embeddings-admin': { minTier: SUBSCRIPTION_TIERS.ELITE },
  'embeddings-admin': { minTier: SUBSCRIPTION_TIERS.ELITE },
  
  // UGC platform (separate plan ladder)
  'app/ugc-scripts': { minTier: SUBSCRIPTION_TIERS.CREATOR, showPreview: false },
  'app/ugc-dashboard': { minTier: SUBSCRIPTION_TIERS.CREATOR, showPreview: false },
  'app/ugc-hub': { minTier: SUBSCRIPTION_TIERS.CREATOR, showPreview: false },
  'app/ugc-brands': { minTier: SUBSCRIPTION_TIERS.CREATOR, showPreview: false },
  'app/ugc-crm': { minTier: SUBSCRIPTION_TIERS.CREATOR, showPreview: false },
  'app/ugc-community': { minTier: SUBSCRIPTION_TIERS.CREATOR, showPreview: false },
  'app/teleprompter': { minTier: SUBSCRIPTION_TIERS.CREATOR, showPreview: false },
  'app/ugc-onboarding': { minTier: SUBSCRIPTION_TIERS.CREATOR, showPreview: false },
  
  // Founder Lifetime - full access to coaching platform
  'app/founder-benefits': { minTier: SUBSCRIPTION_TIERS.FOUNDER },
  'founder-benefits': { minTier: SUBSCRIPTION_TIERS.FOUNDER },
  'app/vip-dashboard': { minTier: SUBSCRIPTION_TIERS.FOUNDER },
  'vip-dashboard': { minTier: SUBSCRIPTION_TIERS.FOUNDER }
};

/**
 * Check if user has access to a feature
 */
export const hasFeatureAccess = (featurePath, userTier = SUBSCRIPTION_TIERS.PRO) => {
  const cleanPath = featurePath?.replace(/^\//, '');
  const feature = FEATURE_ACCESS[cleanPath];
  
  if (!feature) return true; // Allow access if feature not defined
  
  const tierOrder = [
    SUBSCRIPTION_TIERS.PRO,
    SUBSCRIPTION_TIERS.ELITE,
    SUBSCRIPTION_TIERS.CREATOR,
    SUBSCRIPTION_TIERS.CREATOR_ELITE,
    SUBSCRIPTION_TIERS.FOUNDER
  ];
  
  const userTierIndex = tierOrder.indexOf(userTier);
  const requiredTierIndex = tierOrder.indexOf(feature.minTier);
  
  return userTierIndex >= requiredTierIndex;
};

/**
 * Check if a locked feature should show premium preview
 */
export const shouldShowPreview = (featurePath) => {
  const cleanPath = featurePath?.replace(/^\//, '');
  const feature = FEATURE_ACCESS[cleanPath];
  return feature?.showPreview === true;
};

/**
 * Get required tier for a feature
 */
export const getRequiredTier = (featurePath) => {
  const cleanPath = featurePath?.replace(/^\//, '');
  const feature = FEATURE_ACCESS[cleanPath];
  return feature?.minTier || SUBSCRIPTION_TIERS.PRO;
};

/**
 * Get tier display name
 */
export const getTierDisplayName = (tier) => {
  const names = {
    [SUBSCRIPTION_TIERS.PRO]: 'Pro',
    [SUBSCRIPTION_TIERS.ELITE]: 'Elite',
    [SUBSCRIPTION_TIERS.CREATOR]: 'Creator',
    [SUBSCRIPTION_TIERS.CREATOR_ELITE]: 'Creator Elite',
    [SUBSCRIPTION_TIERS.FOUNDER]: 'Founder Lifetime'
  };
  return names[tier] || 'Pro';
};
